import React, { useMemo, useState } from 'react';

import { Badge, Button, Card, Col, Collapse, Popconfirm, Row, Table, Tag, Tooltip } from 'antd';
import { useDeleteMediaOfferGroupMutation, useGetArticleContentQuery, useGetMediaOfferGroupsQuery } from 'services';
import { CircularProgress } from 'components/CircularProgress';
import { DeleteOutlined, EditOutlined, ExportOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { openNotification } from 'utils';
import UploadedMediaModal from 'components/SendItems/UploadedMediaModal';
import MediaThumb from 'components/SendItems/AddMediaContent/MediaThumb';
import { MEDIA_TYPE_LABELS } from 'components/SendItems/AddMediaContent/helpers';
import { useDispatch } from 'react-redux';
import { fetchError } from 'slices/commonSlice';
import { useIntl } from 'react-intl';
import { useMediaActionContext } from 'components/SendItems/AddMediaContent/Context/MediaActioncontext';
import EditMediaContentModal from 'components/SendItems/AddMediaContent/MediaContent/EditMediaContentModal';
import { MEDIA_OFFER_DECIDER, MEDIA_OFFER_NO_PERMISSION } from 'hooks/useMediaOfferPermission';
import { hasDecisionFromDecider } from 'hooks/useMediaOfferDecisionGuard';

import { handleBlobDownload, resolvePosterUrl } from './helpers';
import ContentFile from './ContentFile';
import { DECISION_MAKER_BADGE } from './DecisionMakerBadge';
import MediaOfferUnitBadge from './MediaOfferUnitBadge';
import SupervisorEditMediaContent from './SupervisorEditMediaContent';
import SupervisorAddMediaContent from './SupervisorAddMediaContent';
import DecisionTypeBadge from './DecisionTypeBadge';
import MediaOfferHistory from './MediaOfferHistory';

/**
 * Tablo "Medya" hücresi: ilk dosyanın küçük-resmi (foto/poster/thumbnail — `resolvePosterUrl`
 * ile yüklenen dosya kartlarıyla aynı kural); yoksa tür ikonu fallback'i (`MediaThumb`).
 * Birden fazla dosyada köşede sayı rozeti.
 *
 * Yalnız en az bir dosyası olan satırda render edilir (boş satırı çağıran taraf `-` ile basar);
 * tıklanınca dosya modalını açar.
 *
 * @param {Object} props
 * @param {Object} props.record - Tablo satırı (mediaType + mediaOfferItems taşır)
 * @param {((visible: boolean, data: any[]) => void)} props.onOpen - Dosya modalını açar
 */
const MediaThumbnail = ({ record, onOpen }) => {
  const items = record.mediaOfferItems;
  const count = items.length;
  const firstMedia = items[0]?.media;

  // Dosya modalı her satırda doğru tür ikonunu/önizlemesini çizebilsin diye, medya türünü
  // grup seviyesinden (record.mediaType) her öğeye iliştirerek geçiriyoruz — öğelerin kendi
  // `media` nesnesi foto/video/ses türünü taşımaz.
  const openModal = () =>
    onOpen(
      true,
      items.map((item) => ({ ...item, mediaType: record.mediaType }))
    );

  const thumb = (
    <div
      onClick={openModal}
      style={{ display: 'inline-flex', cursor: 'pointer' }}
    >
      <MediaThumb
        src={resolvePosterUrl(firstMedia, record.mediaType)}
        mediaType={record.mediaType}
        size={44}
        alt={firstMedia?.name || ''}
        bordered
      />
    </div>
  );

  return count > 1 ? (
    <Badge
      count={count}
      size="small"
    >
      {thumb}
    </Badge>
  ) : (
    thumb
  );
};

/**
 * Tablo "Karar" hücresi: satırın MEVCUT kararını ve aktif aşamanın karar beklentisini birlikte gösterir.
 *
 * Neden ikisi birlikte: aşama ilerlediğinde önceki birimin kararı geçersiz olmaz, yalnız aktif
 * aşamanın kararı henüz yoktur. Eskiden bekleyen satırda SADECE "Karar bekliyor" basılıyor, önceki
 * karar gizleniyordu (ör. AB - Medya Teklifi Kontrolü "Uygundur" verdi, madde AB Kontrol'e geçti →
 * satır kararsız görünüyordu). Artık son karar rozeti + (başka birime aitse) kararı veren birim
 * etiketi kalır; turuncu "Karar bekliyor" yalnız aktif aşamanın eksik kararını işaretler.
 *
 * @param {Object} props
 * @param {('approved'|'not_approved')} [props.decisionType] - Güncel versiyonun son karar tipi; yoksa rozet basılmaz (default: yok).
 * @param {string} [props.decidedBy] - Son kararı veren birim (`MEDIA_OFFER_DECIDER`); aktif aşamadan farklıysa etiketlenir (default: yok).
 * @param {boolean} [props.awaitingDecision=false] - Aktif aşamanın bu satıra kararı yok mu (default: false).
 * @param {string|null} [props.currentDecider=null] - Aktif aşamanın karar birimi (default: null).
 */
const MediaDecisionCell = ({ decisionType, decidedBy, awaitingDecision = false, currentDecider = null }) => {
  if (!decisionType && !awaitingDecision) return <>-</>;

  // Birim etiketi yalnız karar BAŞKA bir birime aitse anlamlı: aktif aşamanın kendi kararında
  // rozet tek başına yeterlidir (kimin verdiği zaten bellidir), etiket gürültü olur.
  const foreignDeciderLabel = decisionType && decidedBy !== currentDecider ? DECISION_MAKER_BADGE[decidedBy]?.label : null;

  return (
    <div
      className="gx-d-flex gx-flex-column gx-align-items-start"
      style={{ gap: 4 }}
    >
      {decisionType && <DecisionTypeBadge text={decisionType} />}
      {foreignDeciderLabel && <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)', lineHeight: 1.3 }}>{foreignDeciderLabel} kararı</span>}
      {awaitingDecision && (
        <Tag
          color="orange"
          style={{ margin: 0 }}
        >
          Karar bekliyor
        </Tag>
      )}
    </div>
  );
};

/**
 * ArticleOrderMedia Component
 *
 * Bir makale siparişine (offer) ait içerik ve medya tekliflerini listeleyen,
 * düzenleme/silme/görüntüleme işlemlerini yöneten bileşendir.
 *
 * Bu component:
 * - Madde dosyası, kimlik kartı ve ÜVKA dosyasını gösterir.
 * - Medya tekliflerini tablo halinde listeler.
 * - Gerekli durumlarda medya düzenleme, silme ve indirme işlemlerini sağlar.
 * - Yetkiye ve akış tipine göre (yeni madde gönderme, inceleme, birim kontrol vb.)
 *   farklı aksiyon butonları render eder.
 *
 * @component
 *
 * @param {Object} props
 * @param {number|string} props.offerID
 *  - İlgili makale teklifinin ID’si.
 *  - useGetArticleContentQuery ve useGetMediaOfferGroupsQuery sorgularında kullanılır.
 *
 * Not: Çoklu-medya görüntüleme modalı (`UploadedMediaModal`) bu bileşenin içinde render edilir ve
 * açık/kapalı durumu tamamen içeride tutulur — dışarıdan modal prop'u geçirilmez.
 *
 * @param {boolean} [props.isContentSend=false]
 *  - Component’in "yeni madde gönderme" adımında kullanılıp kullanılmadığını belirler.
 *  - Bu durumda madde dosyası, kimlik kartı ve ÜVKA dosyası gösterilir.
 *
 * @param {boolean} [props.isEncylopediaUnitControl=false]
 *  - Ansiklopedi birimi kontrol aşamasında kullanılır.
 *  - Bu durumda bazı içerikler (örn: yazar açıklaması) gizlenebilir.
 *
 * @param {MediaOfferPermission} [props.permission=MEDIA_OFFER_NO_PERMISSION]
 *  - `useMediaOfferPermission(offer)` dönüşü, OLDUĞU GİBİ. Yetkinin tek kaynağı budur; bu bileşen
 *    (ve onu kullanan hiçbir ekran) yetki kararı vermez, yalnız uygular. Geçilmezse salt-görüntüleme.
 *  - Kullanılan alanlar: `canManage` (aksiyon var mı), `canManageAllRows` (admin muafiyeti),
 *    `limitedEdit` (yalnız Altyazı/Kaynak/Link), `activeDecider` (karar kolonu + karar kartı başlığı),
 *    `actingAs` (silme sahipliği), `isAuthorActor` (yazar satır istisnası).
 *
 * @param {boolean} [props.isSubmitFlow=false]
 *  - Gönderim formu bağlamı (yazar → `AddNewArticleView`, madde sorumlusu → `SupervisorSendItemsView`).
 *  - `true` iken satır tıklaması kararsız `EditMediaContentModal`'ı açar (karar bir inceleme
 *    aksiyonudur, gönderim formunda yeri yoktur) ve "Ekle" butonu basılmaz — o ekranın kendi
 *    ekleme arayüzü (`MediaTypeIcons` + `AddMediaContentModal`) zaten vardır.
 *
 * @param {boolean} [props.hideContentFiles=false]
 *  - "Madde Dosyası / Kimlik Kartı / ÜVKA Dosyası" indirilebilir blok'unu gizler.
 *  - NEDEN: Karar ekranları (CommitteeOpinionExtras, ComitteeAskSupervisorForm) bu dosyaları zaten
 *    <ContentUpload> ile kaldırılabilir alanlar olarak gösterir; bu blok onları ikinci kez (indirme
 *    olarak) basınca çift gösterim oluşuyordu. O ekranlar `true` geçerek blok'u kapatır.
 *  - NASIL: Salt-okunur detay ekranlarında (ArticleOrderDetail / RejectedContentDetail / Publisment)
 *    ContentUpload yoktur ve dosyaların buradan indirilmesi gerekir; default `false` ile blok görünür.
 *
 * @returns {JSX.Element}
 *
 * @example
 * // İnceleyen ekran (heyet/TS): yetki tek kaynaktan (useMediaOfferPermission) türetilir
 * const permission = useMediaOfferPermission(offer);
 * <ArticleOrderMedia
 *   offerID={123}
 *   isContentSend
 *   permission={permission}
 * />
 */
const ArticleOrderMedia = ({
  offerID,
  isContentSend = false,
  isEncylopediaUnitControl = false,
  isCommiteeControlColumnsVisible = false,
  permission = MEDIA_OFFER_NO_PERMISSION,
  isSubmitFlow = false,
  hideContentFiles = false,
}) => {
  // Yetki nesnesi tek yerde açılır; aşağıdaki tüm dallar bu değişkenleri kullanır.
  const {
    canManage,
    canManageAllRows,
    limitedEdit,
    canDecide,
    activeDecider: currentDecider,
    decisionRequired,
    actingAs,
    isAuthorActor,
  } = permission;
  const { data, isLoading, isError } = useGetArticleContentQuery(
    {
      offerID,
    },
    {
      skip: !offerID,
    }
  );

  const { data: mediaOffer } = useGetMediaOfferGroupsQuery({ articleOffer: offerID });
  const [deleteOfferdMedia] = useDeleteMediaOfferGroupMutation();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { func, states } = useMediaActionContext();

  // İşlem geçmişi modalı (tablo sağ-üstündeki tek buton tüm grupların geçmişini açar)
  const [historyOpen, setHistoryOpen] = useState(false);

  // Dosya (medya) modalı state'i. Modal bu bileşenin İÇİNDE render edildiği için state de buraya aittir;
  // dışarıdan prop olarak taşınmaz. Böylece bileşen kendine yeter — komite/inceleme gibi ekranlarda
  // "modal state'ini geçirmeyi unutma" diye bir bug sınıfı oluşamaz (aksi halde onOpen `undefined` olur
  // ve hücre tıklanamaz kalırdı).
  const [mediaModal, setMediaModal] = useState({ visible: false, data: null });
  const openMediaModal = (visible, data) => setMediaModal({ visible, data });

  // Satır aksiyonları — kural seti `useMediaOfferPermission` JSDoc'undaki tasarım kurallarıyla aynıdır:
  //
  //  - canEditRow (kalem/"Düzenle" vs göz/"Detay"): **satır sahipliği düzenlemeyi kısıtlamaz.** Yetkisi
  //    olan kullanıcı tablodaki HER satırı düzenler — inceleyen birim yazardan/önceki birimden gelen
  //    medyayı elden geçirmek zorundadır. Yıkıcı değildir: `/revise` yeni versiyon üretir, önceki
  //    birimin orijinali versiyon zincirinde ve işlem geçmişinde durur.
  //    Tek istisna YAZAR'dır (`isAuthorActor`): yazar heyetin/birimlerin eklediklerini görür ama
  //    onlara müdahale edemez → yalnız kendi eklediği satırları düzenler.
  //  - canDeleteRow (çöp/"Sil"): **yalnız satırı EKLEYEN birim.** `DELETE /media_offer_groups/{id}`
  //    grubu ve TÜM versiyonlarını (önceki birimin orijinali + işlem geçmişi dahil) geri dönüşsüz
  //    sildiği için başkasının eklediği satır silinemez. Sınırlı modda (YM redaksiyonu) silme yoktur.
  //
  // Sahiplik `createdByRole` ile ölçülür — bilinçli olarak `decisionMaker` DEĞİL: `decisionMaker`
  // "en son kim dokundu"yu tutar ve her revizede kayar, dolayısıyla sahiplik olarak kullanılırsa
  // düzenleyen kişi satırı kendi kendine kilitler.
  const canEditRow = (record) => {
    if (!canManage) return false;
    if (canManageAllRows) return true;
    if (isAuthorActor) return record.createdByRole === MEDIA_OFFER_DECIDER.author;
    return true;
  };

  const canDeleteRow = (record) => {
    if (!canManage || limitedEdit) return false;
    if (canManageAllRows) return true;
    return Boolean(actingAs) && record.createdByRole === actingAs;
  };

  const articleContent = useMemo(() => {
    return data?.contents.find((item) => item.type === 0);
  }, [data]);

  const idCard = useMemo(() => {
    return data?.contents.find((item) => item.type === 1);
  }, [data]);

  const authorDescription = useMemo(() => {
    return data?.authorDescription;
  }, [data]);

  const isAuthorDescriptionShown = useMemo(() => {
    return isContentSend && authorDescription && !isEncylopediaUnitControl;
  }, [isContentSend, authorDescription, isEncylopediaUnitControl]);

  const uvkaFile = useMemo(() => {
    return data?.contents.find((item) => item.type === 3);
  }, [data]);

  // bağlanmış olan bir maddeyi silmek için kullanılır
  const handleDelete = async (id) => {
    const { error } = await deleteOfferdMedia({
      id,
    });
    if (error) {
      return dispatch(
        fetchError(
          formatMessage(
            {
              id: 'article.form.fetchError',
            },
            { error: error.status }
          )
        )
      );
    }
    openNotification({
      message: formatMessage({
        id: 'appModule.actionSuccessfully',
      }),
    });
  };

  // yeni madde yollama ekranındaki medya teklifini düzenlemek için tablodaki icona basınca ilgili alanları form a set etmek için kullanılır
  // record.versionId → güncel versiyon (revize/karar hedefi), record.key → grup id (grup detayı + işlem geçmişi)
  const handleSetEdit = (record, event) => {
    // Tetikleyen elemanın focus'unu bırak ki modal açılınca aria-hidden a11y uyarısı çıkmasın
    event?.currentTarget?.blur?.();
    func.handleEditStateTrigger({
      trigger: true,
      mediaID: record.versionId,
      groupId: record.key,
    });
  };

  // Tablonun aksiyon kolonu (yazar gönderme + inceleme akışları aynı kalıbı paylaşır — ikisi aynı
  // anda asla render edilmez). Etiket/ikon `canEditRow`'dan, silme ikonu `canDeleteRow`'dan türer:
  // satırı düzenleyebilen birincil "Düzenle" (primary/mavi, ✏️), silebilen ek olarak "Sil" (danger, 🗑)
  // görür; yalnız inceleyen ise
  // sakin "Detay" (nötr, 👁) görür. Butonlar ikon-only — fark renkle + ikonla okunur, tooltip ile
  // de adlandırılır. Böylece tabloda yönetilen satırlar "renkli/aktif", incelenenler "sakin/nötr" olur.
  const actionColumn = {
    key: 'action',
    width: 110,
    render: (record) => {
      const editable = canEditRow(record);
      const deletable = canDeleteRow(record);
      return (
        <div
          className="gx-d-flex gx-align-items-center"
          style={{ gap: 8 }}
        >
          <Tooltip title={editable ? 'Düzenle' : 'Detay'}>
            <Button
              type={editable ? 'primary' : 'default'}
              icon={editable ? <EditOutlined /> : <EyeOutlined />}
              onClick={(e) => handleSetEdit(record, e)}
              aria-label={editable ? 'Düzenle' : 'Detay'}
              // Global button.less her .ant-btn'e margin-bottom:15px + (son çocuk değilse) margin-right:15px
              // verir → ikon-buton grubu sıkışıp kareliğini kaybeder. Grupta margini sıfırlayıp flex
              // ezilmesini kapatıyoruz; boşluk yalnız container'ın gap:8'inden gelsin.
              style={{ margin: 0, flex: 'none' }}
            />
          </Tooltip>
          {deletable && (
            <Popconfirm
              placement="leftBottom"
              title="Silmek İstediğinizden Emin misiniz?"
              okText="Evet"
              cancelText="Vazgeç"
              onConfirm={() => handleDelete(record.key)}
            >
              <Tooltip title="Sil">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Sil"
                  style={{ margin: 0, flex: 'none' }}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      );
    },
  };

  // Birim (medyayı İLK oluşturan birim = grup kökündeki `createdByRoleUnit`). Aşama adı değil kurumsal
  // birim basılır (ör. "İH - Madde Kontrol" değil "İlim Heyeti"). Bilinçli olarak
  // `currentVersion.decisionMaker` DEĞİL: o alan "bu aşamada kararı kim verdi"yi tutar, revize/kontrol
  // ile değişir → heyet/kontrol biri güncellediğinde Birim onun adına kaymamalı (sabit kalmalı).
  // Yazar gönderme akışında her zaman "author" olduğu için gizlenir; diğer ekranlarda (heyet/TS inceleme,
  // telif sonrası görünümler) oluşturan birim yazardan farklı olabileceği için gösterilir.
  const birimColumn = {
    title: 'Birim',
    dataIndex: 'createdByRoleUnit',
    key: 'createdByRoleUnit',
    width: 150,
    render: (unit) => <MediaOfferUnitBadge unit={unit} />,
  };

  // Kolon sırası ürün talebiyle sabitlendi: Birim | Dosya Türü | Medya | Link | Açıklama | Altyazı | Kaynak | Karar | İşlemler
  const colums = [
    {
      title: 'Dosya Türü',
      dataIndex: 'mediaType',
      key: 'mediaType',
      width: 110,
      render: (text) => MEDIA_TYPE_LABELS[text] || '-',
    },
    {
      title: 'Medya',
      dataIndex: 'media',
      key: 'media',
      width: 90,
      // Medya içeren HER satır dosya modalını açar (tek ya da çoklu fark etmez). Modal her
      // bağlamda anlamlı bir aksiyon sunar: görselde büyük-görüntüleme (göz) + bağlama göre
      // indirme (isContentSend) ya da silme (yazar/inceleme). Dosyası olmayan grupta tür ikonu
      // basılmaz: tıklanamayan ikon dosya varmış gibi okunuyordu; diğer boş hücreler gibi `-`.
      render: (_, record) =>
        record.mediaOfferItems?.length ? (
          <MediaThumbnail
            record={record}
            onOpen={openMediaModal}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      width: 80,
      align: 'center',
      render: (text) =>
        text ? (
          <a
            href={text}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Linki aç"
          >
            <ExportOutlined style={{ fontSize: 16 }} />
          </a>
        ) : (
          <ExportOutlined style={{ fontSize: 16, color: 'rgba(0,0,0,0.25)' }} />
        ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Altyazı',
      dataIndex: 'subtitle',
      key: 'subtitle',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  const isCommiteeColumns = [
    {
      title: 'Karar',
      dataIndex: 'decisionType',
      key: 'decisionType',
      // Hücre iki bilgiyi birlikte taşır (bkz. MediaDecisionCell): (a) mevcut karar rozeti —
      // önceki birimin kararı aşama ilerlerken kaybolmaz, (b) aktif aşamanın eksik kararını
      // işaretleyen "Karar bekliyor" — kullanıcı aşamayı onaylayamamasının hangi satırdan
      // geldiğini görebilsin (bkz. useMediaOfferDecisionGuard).
      render: (text, record) => (
        <MediaDecisionCell
          decisionType={text}
          decidedBy={record.decidedBy}
          awaitingDecision={record.awaitingDecision}
          currentDecider={currentDecider}
        />
      ),
    },
  ];

  // Aksiyon kolonu HER akışta render edilir: düzenlenebilen satır "Düzenle", diğerleri
  // "Detay" (salt-görüntüleme) gösterir (bkz. actionColumn / canEditRow). Böylece yönetme yetkisi
  // olmayan izleyiciler de her ekranda medya detayını (read-only modal) açabilir; salt-görüntüleme
  // ekranlarında (madde-detay vb.) da Detay artık görünür.
  const tableColumns = [...(isContentSend ? [birimColumn] : []), ...colums, ...(isCommiteeControlColumnsVisible ? isCommiteeColumns : []), actionColumn];
  // Yeni grup modeli: görünür alanlar `currentVersion.*` ve `currentVersion.lastDecision.*` altında gömülü gelir.
  // Tablo satırı yalnız listede render edilen alanları düz alanlara map'ler (detaylar geçmiş modalinde ayrı
  // sorgudan gelir); `key` = grup id (silme/geçmiş), `versionId` = güncel versiyon id (revize/karar).
  const dataSource = useMemo(
    () =>
      (mediaOffer || []).map((item) => {
        const version = item.currentVersion;
        const decision = version?.lastDecision;
        return {
          key: item.id,
          versionId: version?.id,
          mediaType: item.mediaType,
          // Birim kolonu: medyayı İLK oluşturan birim (grup kökünde, versiyondan bağımsız sabit).
          // Revize/karar sırasında değişmez — bkz. birimColumn.
          createdByRole: item.createdByRole,
          // Birim kolonunun gösterimi; yetki `createdByRole` ile ölçülmeye devam eder (birim, aşamaları birleştirir).
          createdByRoleUnit: item.createdByRoleUnit,
          // Yetki (canEditRow/canDeleteRow): güncel versiyonun decider'ı — üst birim revize edince sahiplik ona geçsin.
          decisionMaker: version?.decisionMaker,
          mediaOfferItems: version?.mediaOfferItems,
          description: version?.description,
          subtitle: version?.subtitle,
          source: version?.source,
          link: version?.link,
          decisionType: decision?.decisionType,
          // Kararı veren birim: "Karar" hücresi, karar aktif aşamadan farklı bir birime aitse
          // rozetin altına o birimin adını yazar (önceki aşamanın kararı olduğu anlaşılsın).
          decidedBy: decision?.decidedBy,
          // Aktif aşamanın (currentDecider) bu gruba kararı var mı — "Karar" kolonundaki
          // "Karar bekliyor" işaretini besler. İşaret yalnız kararın zorunlu olduğu aşamada
          // (`decisionRequired`, yalnız 36) çıkar; diğer aşamalarda karar opsiyoneldir.
          awaitingDecision: decisionRequired && !hasDecisionFromDecider(item, currentDecider),
        };
      }),
    [mediaOffer, currentDecider, decisionRequired]
  );

  // İşlem Geçmişi tüm grupları kapsar: olay-bazlı history API'sinde her grubun en az bir
  // `created` kaydı vardır, dolayısıyla "geçmişi olmayan grup" durumu kalmadı (eski
  // versiyon-bazlı filtre — version > 1 || decisionType — bu yüzden kaldırıldı).
  // Sorgular yine modal açılınca atılır (destroyOnClose), grup başına gereksiz istek yoktur.
  const historyGroups = mediaOffer || [];
  const hasMediaHistory = historyGroups.length > 0;

  // Yeni medya teklifi ekleyebilir miyiz? Sınırlı moddaki (YM redaksiyonu) birimler ekleyemez; gönderim
  // formunun kendi ekleme arayüzü (MediaTypeIcons + AddMediaContentModal) zaten var, orada tekrarlanmaz.
  const canAddMedia = isContentSend && canManage && !limitedEdit && !isSubmitFlow;

  // Medya bloğu (başlık + "Ekle" + tablo) ne zaman basılır: satır varsa ya da ekleme yetkisi varsa.
  //
  // NEDEN ikincisi: blok eskiden yalnız `dataSource.length > 0` ile basılıyordu, yani MEDYASI HİÇ OLMAYAN
  // bir maddede "Ekle" butonu da gizleniyordu → ekleme yetkisi olan birim ilk medyayı hiçbir zaman
  // ekleyemiyordu. Yazar/heyet gönderim ekranında fark edilmiyordu (orada ayrı bir ekleme arayüzü var),
  // telif sonrası birimlerine ekleme açılınca ortaya çıktı. Boş tabloyu antd'nin kendi "Veri Yok"
  // durumuyla basıyoruz: kullanıcı hem medyanın olmadığını görür hem ekleyebilir.
  //
  // Yetkisi olmayan izleyicide (yalnız görüntüleme) boş blok basılmaz — gösterecek de yaptıracak da
  // bir şey yoktur, boş bir kart gürültüdür.
  const showMediaBlock = dataSource.length > 0 || canAddMedia;

  // Detay modalının düzenlenebilir mi yoksa salt-görüntüleme mi açılacağı, tıklanan satırın
  // düzenlenebilirliğine bağlıdır (butonun "Düzenle"/"Detay" etiketiyle BİREBİR aynı kaynak: canEditRow).
  // Düzenlenemeyen satır (ör. başka birimin eklediği ya da izleyicinin yetkisi yok) veya modal kapalıyken
  // → read-only. limitedEdit modda canEditRow tüm satırlara açıktır (altyazı/kaynak/link editable olacak).
  // Yazar akışı ayrı modal (EditMediaContentModal) kullandığı için buradan etkilenmez.
  const triggeredRow = dataSource.find((row) => row.versionId === states.editTrigger.mediaID);
  const isTriggeredRowEditable = triggeredRow ? canEditRow(triggeredRow) : false;

  if (isLoading) return <CircularProgress />;

  return (
    <>
      <Card
        bordered={false}
        style={{
          display: (!isContentSend || canManage || isEncylopediaUnitControl || isError) && !showMediaBlock ? 'none' : 'block',
        }}
      >
        {isContentSend && !isEncylopediaUnitControl && !hideContentFiles && (
          <Row gutter={24}>
            {articleContent && (
              <Col
                sm={24}
                md={12}
              >
                <h4>Madde Dosyası</h4>
                <ContentFile
                  fileName={articleContent.media[0].name}
                  fileUrl={articleContent.media[0].fileFullUrl}
                />
              </Col>
            )}

            {idCard && (
              <Col
                sm={24}
                md={12}
              >
                <h4>Kimlik Kartı</h4>
                <ContentFile
                  fileName={idCard.media[0].name}
                  fileUrl={idCard.media[0].fileFullUrl}
                />
              </Col>
            )}
            {uvkaFile && (
              <Col
                sm={24}
                md={idCard ? 24 : 12}
              >
                <h4>ÜVKA Dosyası</h4>
                <ContentFile
                  fileName={uvkaFile.media[0].name}
                  fileUrl={uvkaFile.media[0].fileFullUrl}
                />
              </Col>
            )}
          </Row>
        )}

        {showMediaBlock && (
          <>
            {(isContentSend || hasMediaHistory) && (
              <div className="gx-d-flex gx-justify-content-between gx-align-items-center gx-mb-3">
                {isContentSend && <h4 className="gx-mb-0">Medya Teklifi</h4>}
                <div className="gx-d-flex gx-align-items-center">
                  {canAddMedia && <SupervisorAddMediaContent />}
                  {hasMediaHistory && (
                    <Button
                      className="gx-mb-0"
                      icon={<HistoryOutlined />}
                      onClick={(e) => {
                        e.currentTarget.blur();
                        setHistoryOpen(true);
                      }}
                    >
                      İşlem Geçmişi
                    </Button>
                  )}
                </div>
              </div>
            )}
            <Table
              loading={isLoading}
              scroll={{ x: 1000 }}
              rowKey="key"
              bordered
              columns={tableColumns}
              dataSource={dataSource}
              pagination={dataSource.length > 10}
            />
          </>
        )}
        {isAuthorDescriptionShown && (
          <Collapse className="gx-mt-5">
            <Collapse.Panel
              header="Açıklamalar ve Notlar"
              key="1"
            >
              <p>{authorDescription}</p>
            </Collapse.Panel>
          </Collapse>
        )}
      </Card>
      {mediaModal?.visible && (
        <UploadedMediaModal
          isContentSend={isContentSend}
          downloadBlob={handleBlobDownload}
          handleDeleteFormMedia={handleDelete}
          onClose={() => openMediaModal(false, null)}
          open={mediaModal?.visible}
          data={mediaModal?.data}
        />
      )}
      {/**
       * Düzenleme/detay modalı akışa + tıklanan satıra göre seçilir; tetik ile aynı (bu ağaçtaki)
       * provider'da render edilir ki yazarda heyet karar bölümü açılmasın (madde-9):
       * - gönderim formu (isSubmitFlow) → kararsız EditMediaContentModal (karar inceleme ekranlarına ait)
       * - diğer tüm akışlar → SupervisorEditMediaContent; tıklanan satır yönetilebilir DEĞİLSE read-only
       *   (footer + karar kartı gizli, alanlar salt-okunur), yönetilebilirse düzenleme + karar açık.
       */}
      {isSubmitFlow ? (
        <EditMediaContentModal mediaID={states.editTrigger.mediaID} />
      ) : (
        <SupervisorEditMediaContent
          mediaID={states.editTrigger.mediaID}
          currentDecider={currentDecider}
          actingAs={actingAs}
          canDecide={canDecide}
          readOnly={!isTriggeredRowEditable}
          limitedEdit={limitedEdit}
        />
      )}
      <MediaOfferHistory
        articleOffer={offerID}
        groups={historyGroups}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

export default ArticleOrderMedia;
